

import React, { useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import {
  Row, Col, Card, Button, Typography, Descriptions, Tag, 
  notification, Spin, Avatar, Divider, Modal, Form, Input,
  Space, Tooltip, Timeline, Alert, Popconfirm
} from "antd";
import axios from "axios";
import { baseUrl } from "../../config";
import {
  ArrowLeftOutlined, EditOutlined, BookOutlined, 
  CheckCircleOutlined, CloseCircleOutlined, CalendarOutlined,
  CheckOutlined, CloseOutlined, ExclamationCircleOutlined,
  HistoryOutlined, UserOutlined, CommentOutlined
} from "@ant-design/icons";
import moment from 'moment';
import { hasPermission } from '../../components/permissions';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { confirm } = Modal;

function PurposesView({ purposeId = null, onEdit = null, isModal = false }) {
  const { id: paramId } = useParams();
  const history = useHistory();
  const id = purposeId || paramId;
  
  const [purpose, setPurpose] = useState(null);
  const [loading, setLoading] = useState(false);
  const [approvalHistory, setApprovalHistory] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [approvalModalVisible, setApprovalModalVisible] = useState(false);
  const [approvalAction, setApprovalAction] = useState('approve'); // 'approve' or 'reject'
  const [form] = Form.useForm();

  useEffect(() => {
    if (id) {
      fetchPurposeDetails();
      fetchApprovalHistory();
      fetchPendingApprovals();
    }
  }, [id]);

  const fetchPurposeDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${baseUrl}/purposes/read/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });
      
      if (response.status === 200) {
        setPurpose(response.data.result);
      }
    } catch (error) {
      notification.error({
        message: 'Error',
        description: 'Failed to fetch purpose details',
        placement: 'topRight'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchApprovalHistory = async () => {
    try {
      const response = await axios.get(`${baseUrl}/purposes/${id}/approval-history`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });
      
      if (response.status === 200) {
        setApprovalHistory(response.data.result || []);
      }
    } catch (error) {
      console.log('No approval history found');
      setApprovalHistory([]);
    }
  };

  const fetchPendingApprovals = async () => {
    try {
      const response = await axios.get(`${baseUrl}/purposes/${id}/pending-approvals`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });
      
      if (response.status === 200) {
        setPendingApprovals(response.data.result || []);
      }
    } catch (error) {
      console.log('No pending approvals found');
      setPendingApprovals([]);
    }
  };

  const handleApprovalAction = async (values) => {
    try {
      const endpoint = approvalAction === 'approve' ? 'approve' : 'reject';
      const response = await axios.post(
        `${baseUrl}/purposes/${id}/${endpoint}`,
        {
          comments: values.comments,
          ...values
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
        }
      );

      if (response.status === 200) {
        notification.success({
          message: 'Success',
          description: `Purpose ${approvalAction}d successfully!`,
          placement: 'topRight'
        });
        
        setApprovalModalVisible(false);
        form.resetFields();
        fetchPurposeDetails();
        fetchApprovalHistory();
        fetchPendingApprovals();
      }
    } catch (error) {
      notification.error({
        message: 'Error',
        description: error.response?.data?.message || `Failed to ${approvalAction} purpose`,
        placement: 'topRight'
      });
    }
  };

  const showApprovalModal = (action) => {
    setApprovalAction(action);
    setApprovalModalVisible(true);
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Student_Support': 'blue',
      'Staff_Compensation': 'green',
      'Infrastructure': 'orange',
      'Operations': 'purple',
      'Medical_Aid': 'red',
      'Education': 'cyan',
      'Technology': 'geekblue',
      'Marketing': 'magenta',
      'Legal': 'volcano',
      'Other': 'default'
    };
    return colors[category] || 'default';
  };

  const getApprovalStatusColor = (status) => {
    const colors = {
      'approved': 'success',
      'rejected': 'error',
      'pending': 'warning',
      'draft': 'default'
    };
    return colors[status] || 'default';
  };

  const renderApprovalButtons = () => {
    if (!purpose?.requires_approval || !hasPermission('/purposes', 'editPermission')) {
      return null;
    }

    const hasApprovalPermission = hasPermission('/purposes', 'editPermission');
    const isPending = purpose.approval_status === 'pending';

    if (!hasApprovalPermission || !isPending) {
      return null;
    }

    return (
      <Space size="middle">
        <Button
          type="primary"
          icon={<CheckOutlined />}
          onClick={() => showApprovalModal('approve')}
          style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
        >
          Approve
        </Button>
        <Button
          danger
          icon={<CloseOutlined />}
          onClick={() => showApprovalModal('reject')}
        >
          Reject
        </Button>
      </Space>
    );
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!purpose) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Text>Purpose not found</Text>
      </div>
    );
  }

  return (
    <div style={{ padding: isModal ? '0' : '24px' }}>
      <Row gutter={[24, 24]}>
        <Col span={24}>
          <Card>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                {!isModal && (
                  <Button 
                    icon={<ArrowLeftOutlined />} 
                    onClick={() => history.goBack()}
                    size="large"
                  />
                )}
                <Avatar size={64} icon={<BookOutlined />} style={{ backgroundColor: '#52c41a' }} />
                <div>
                  <Title level={2} style={{ margin: 0 }}>{purpose.name}</Title>
                  <div style={{ marginTop: '8px' }}>
                    <Tag color="blue" style={{ marginRight: '8px' }}>{purpose.code}</Tag>
                    <Tag color={getCategoryColor(purpose.category)}>
                      {purpose.category?.replace('_', ' ')}
                    </Tag>
                    {purpose.approval_status && (
                      <Tag color={getApprovalStatusColor(purpose.approval_status)}>
                        {purpose.approval_status?.toUpperCase()}
                      </Tag>
                    )}
                  </div>
                </div>
              </div>
              <Space>
                {renderApprovalButtons()}
                {(hasPermission('/purposes', 'editPermission') && (!isModal || onEdit)) && (
                  <Button
                    icon={<EditOutlined />}
                    onClick={() => isModal ? onEdit(id) : history.push(`/purposes/update/${id}`)}
                    size="large"
                    style={{ background: '#7a0000', borderColor: '#7a0000', color: '#fff' }}
                  >
                    Edit Purpose
                  </Button>
                )}
              </Space>
            </div>

            {/* Approval Status Alert */}
            {purpose.requires_approval && purpose.approval_status === 'pending' && (
              <Alert
                message="Approval Required"
                description="This purpose requires approval before it can be used for expenses."
                type="warning"
                showIcon
                style={{ marginBottom: '24px' }}
              />
            )}

            <Row gutter={[24, 24]}>
              {/* Basic Information */}
              <Col xs={24} lg={12}>
                <Card title="Basic Information" size="small">
                  <Descriptions column={1} bordered size="small">
                    <Descriptions.Item 
                      label={<><BookOutlined /> Purpose Name</>}
                    >
                      {purpose.name}
                    </Descriptions.Item>
                    <Descriptions.Item 
                      label="Purpose Code"
                    >
                      <Tag color="blue">{purpose.code}</Tag>
                    </Descriptions.Item>
                    <Descriptions.Item 
                      label="Category"
                    >
                      <Tag color={getCategoryColor(purpose.category)}>
                        {purpose.category?.replace('_', ' ')}
                      </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item 
                      label="Sub Category"
                    >
                      {purpose.sub_category || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item 
                      label="Description"
                    >
                      {purpose.description || '-'}
                    </Descriptions.Item>
                  </Descriptions>
                </Card>
              </Col>

              {/* Configuration */}
              <Col xs={24} lg={12}>
                <Card title="Configuration" size="small">
                  <Descriptions column={1} bordered size="small">
                    <Descriptions.Item 
                      label="Maximum Amount Limit"
                    >
                      {purpose.max_amount_limit ? 
                        `₹${Number(purpose.max_amount_limit).toLocaleString('en-IN')}` : 
                        'No Limit'
                      }
                    </Descriptions.Item>
                    <Descriptions.Item 
                      label="Is Recurring"
                    >
                      {purpose.is_recurring ? (
                        <Tag color="success" icon={<CheckCircleOutlined />}>Yes</Tag>
                      ) : (
                        <Tag color="default" icon={<CloseCircleOutlined />}>No</Tag>
                      )}
                    </Descriptions.Item>
                    <Descriptions.Item 
                      label="Requires Approval"
                    >
                      {purpose.requires_approval ? (
                        <Tag color="warning" icon={<CheckCircleOutlined />}>Required</Tag>
                      ) : (
                        <Tag color="success" icon={<CloseCircleOutlined />}>Not Required</Tag>
                      )}
                    </Descriptions.Item>
                    <Descriptions.Item 
                      label="Status"
                    >
                      <Tag color={purpose.status === 'Active' ? 'success' : 'default'}>
                        {purpose.status}
                      </Tag>
                    </Descriptions.Item>
                    {purpose.approval_status && (
                      <Descriptions.Item 
                        label="Approval Status"
                      >
                        <Tag color={getApprovalStatusColor(purpose.approval_status)}>
                          {purpose.approval_status?.toUpperCase()}
                        </Tag>
                      </Descriptions.Item>
                    )}
                  </Descriptions>
                </Card>
              </Col>

              {/* Approval History */}
              {purpose.requires_approval && approvalHistory.length > 0 && (
                <Col span={24}>
                  <Card title={<><HistoryOutlined /> Approval History</>} size="small">
                    <Timeline
                      items={approvalHistory.map((item, index) => ({
                        color: item.action === 'approved' ? 'green' : item.action === 'rejected' ? 'red' : 'blue',
                        children: (
                          <div>
                            <div style={{ fontWeight: 'bold' }}>
                              {item.action?.toUpperCase()} by {item.approved_by || item.user_name}
                            </div>
                            <div style={{ fontSize: '12px', color: '#666' }}>
                              {moment(item.created_at).format('DD MMM YYYY, HH:mm')}
                            </div>
                            {item.comments && (
                              <div style={{ marginTop: '4px', fontStyle: 'italic' }}>
                                "{item.comments}"
                              </div>
                            )}
                          </div>
                        )
                      }))}
                    />
                  </Card>
                </Col>
              )}

              {/* System Information */}
              <Col span={24}>
                <Card title="System Information" size="small">
                  <Descriptions column={2} bordered size="small">
                    <Descriptions.Item 
                      label={<><CalendarOutlined /> Created On</>}
                    >
                      {moment(purpose.created_at).format('DD MMM YYYY, HH:mm')}
                    </Descriptions.Item>
                    <Descriptions.Item 
                      label={<><CalendarOutlined /> Last Updated</>}
                    >
                      {moment(purpose.updated_at).format('DD MMM YYYY, HH:mm')}
                    </Descriptions.Item>
                    <Descriptions.Item 
                      label="Created By"
                    >
                      {purpose.created_by || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item 
                      label="Purpose ID"
                    >
                      {purpose.id}
                    </Descriptions.Item>
                    {purpose.approved_by && (
                      <Descriptions.Item 
                        label="Approved By"
                      >
                        {purpose.approved_by}
                      </Descriptions.Item>
                    )}
                    {purpose.approval_date && (
                      <Descriptions.Item 
                        label="Approval Date"
                      >
                        {moment(purpose.approval_date).format('DD MMM YYYY, HH:mm')}
                      </Descriptions.Item>
                    )}
                  </Descriptions>
                </Card>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {/* Approval Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {approvalAction === 'approve' ? (
              <CheckCircleOutlined style={{ color: '#52c41a' }} />
            ) : (
              <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
            )}
            {approvalAction === 'approve' ? 'Approve Purpose' : 'Reject Purpose'}
          </div>
        }
        open={approvalModalVisible}
        onCancel={() => {
          setApprovalModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={500}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleApprovalAction}
        >
          <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '6px' }}>
            <Text strong>Purpose: </Text>{purpose.name} ({purpose.code})
          </div>

          <Form.Item
            label="Comments"
            name="comments"
            rules={[
              { required: approvalAction === 'reject', message: 'Comments are required for rejection' }
            ]}
          >
            <TextArea
              rows={4}
              placeholder={
                approvalAction === 'approve' 
                  ? 'Optional: Add approval comments...' 
                  : 'Please provide reason for rejection...'
              }
            />
          </Form.Item>

          <div style={{ textAlign: 'right' }}>
            <Space>
              <Button onClick={() => {
                setApprovalModalVisible(false);
                form.resetFields();
              }}>
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                danger={approvalAction === 'reject'}
                style={approvalAction === 'approve' ? { backgroundColor: '#52c41a', borderColor: '#52c41a' } : {}}
              >
                {approvalAction === 'approve' ? 'Approve' : 'Reject'}
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>
    </div>
  );
}

export default PurposesView;